import { useEffect, useMemo, useRef } from 'react';
import { useTracking } from 'react-tracking';
import { useTheme } from '@simplywallst/ui-core';
import { localPoint } from '@visx/event';
import { maxBy, minBy, orderBy } from 'lodash';
import type { ScaleTime, ScaleLinear } from 'd3-scale';
import type { ChartData, PeriodType } from '../../hooks/useMarketPerformance';
import { VerticalCrosshair, MarkerWrapper } from './styled';
import { GlyphDot } from './GlyphDot';
import { Legend } from './Legend';

import type { ValuationType } from './MarketPerformanceChart';
import { bisectDate, peChartHeight } from './MarketPerformanceChart';

import { VisxText } from '@/components/VisxText';

interface Props {
  width: number;
  height: number;
  keys: number[];
  chartData: ChartData;
  datum: ChartData[number];
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  yScaleValuationRelative: ScaleLinear<number, number>;
  period: PeriodType;
  index: number;
  setIndex: (index: number) => void;
  maxValuation: number;
  isShowingDots: boolean;
  peMean: number;
  valuationType: ValuationType;
  verticalPadding: number;
}

const legendHeight = 24;
const labelHeight = 22;

export const Tooltip = ({
  height,
  keys,
  xScale,
  yScale,
  yScaleValuationRelative,
  width,
  index,
  setIndex,
  datum,
  maxValuation,
  isShowingDots,
  peMean,
  valuationType,
  verticalPadding,
}: Props) => {
  const theme = useTheme();
  const x = xScale(keys[index]) || 0;
  useEffect(() => {
    if (keys.length > 0 && index > keys.length - 1) {
      setIndex(keys.length - 1);
    }
  }, [keys, index, setIndex]);

  const { trackEvent } = useTracking();
  const firstHover = useRef(false);

  const handleMove = (e) => {
    const point = localPoint(e);
    const time = xScale.invert(point ? point.x : 0);
    const nextIndex = bisectDate(keys, time);
    if (nextIndex !== index && nextIndex <= keys.length - 1) {
      setIndex(nextIndex);
    }
    if (!firstHover.current) {
      trackEvent({
        type: 'track',
        action: 'hover',
        subject: 'Market Performance Valuation Chart',
      });
      firstHover.current = true;
    }
  };

  const defaultWidth = width - verticalPadding * 2 - 5;
  const isNearLeftAxis = index > (width < 480 ? 10 : 5);
  const movementPosition = useMemo(() => {
    const marketCap = {
      y: yScaleValuationRelative(datum.marketCapDiff) || 0,
      dy: 8,
    };
    const revenue = {
      y: yScaleValuationRelative(datum.revenueDiff) || 0,
      dy: 8,
    };
    const earnings = {
      y: yScaleValuationRelative(datum.earningsDiff) || 0,
      dy: 8,
    };
    if (index === 0) {
      marketCap.dy -= labelHeight;
      earnings.dy = 8;
      revenue.dy += labelHeight;
    } else {
      const groups = [marketCap, revenue, earnings];
      const orderedGroups = orderBy(groups, 'y');
      const maxG = maxBy(groups, 'y');
      const minG = minBy(groups, 'y');
      if (orderedGroups[2].y - orderedGroups[1].y < labelHeight && maxG) {
        maxG.dy += labelHeight - (orderedGroups[2].y - orderedGroups[1].y);
      }
      if (orderedGroups[1].y - orderedGroups[0].y < 22 && minG) {
        minG.dy -= labelHeight - (orderedGroups[1].y - orderedGroups[0].y);
      }
    }
    return {
      marketCap,
      revenue,
      earnings,
    };
  }, [datum, yScaleValuationRelative, index]);

  return (
    <MarkerWrapper>
      <svg width={width} height={height + 88}>
        {keys.length > 0 && (
          <>
            <Legend
              x={verticalPadding}
              y={height}
              width={(defaultWidth * datum.marketCap) / maxValuation}
              color={theme.color.chart06}
              text={`Total Market Cap: ${datum.marketCapFormatted}`}
              isTextInside={datum.marketCap / maxValuation > 0.5}
            />
            <Legend
              x={verticalPadding}
              y={height + legendHeight + 4}
              width={(defaultWidth * datum.earnings) / maxValuation}
              color={theme.color.chart02}
              text={`Total Earnings: ${datum.earningsFormatted}`}
              isTextInside={datum.earnings / maxValuation > 0.5}
            />
            <Legend
              x={verticalPadding}
              y={height + (legendHeight + 4) * 2}
              width={(defaultWidth * datum.revenue) / maxValuation}
              color={theme.color.chart01RGBA(0.5)}
              text={`Total Revenue: ${datum.revenueFormatted}`}
              isTextInside={datum.revenue / maxValuation > 0.5}
            />
            <VisxText
              x={verticalPadding}
              y={peChartHeight + 32}
              fill={theme.color.whiteRGBA(0.5)}
              typography="tiny"
            >
              Total Market Cap vs Earnings and Revenue
            </VisxText>
            {isShowingDots && (
              <>
                <VerticalCrosshair
                  width={1}
                  x={x}
                  y={0}
                  height={height - 32}
                  fill={theme.color.chart01}
                  hover={true}
                />
                <GlyphDot
                  color={theme.color.white}
                  x={x as number}
                  y={yScale(datum[valuationType]) as number}
                  showingMovement={false}
                  backgroundColor={theme.color.darkerGrayRGBA(0.8)}
                />
                <GlyphDot
                  color={theme.color.neutral}
                  x={x as number}
                  y={yScale(peMean) as number}
                  showingMovement={false}
                  backgroundColor={theme.color.darkerGrayRGBA(0.8)}
                />
                <GlyphDot
                  color={theme.color.chart06}
                  x={x as number}
                  y={movementPosition.marketCap.y}
                  showingMovement
                  movement={datum.marketCapDiff}
                  isNearLeftAxis={isNearLeftAxis}
                  dy={movementPosition.marketCap.dy}
                  backgroundColor={theme.color.darkerGrayRGBA(0.8)}
                />
                <GlyphDot
                  color={theme.color.chart02}
                  x={x as number}
                  y={movementPosition.earnings.y}
                  showingMovement
                  movement={datum.earningsDiff}
                  isNearLeftAxis={isNearLeftAxis}
                  dy={movementPosition.earnings.dy}
                  backgroundColor={theme.color.darkerGrayRGBA(0.8)}
                />
                <GlyphDot
                  color={theme.color.chart01}
                  x={x as number}
                  y={movementPosition.revenue.y}
                  showingMovement
                  movement={datum.revenueDiff}
                  isNearLeftAxis={isNearLeftAxis}
                  dy={movementPosition.revenue.dy}
                  backgroundColor={theme.color.darkerGrayRGBA(0.8)}
                />
              </>
            )}
            <rect
              width="100%"
              height={height}
              fill="transparent"
              onMouseMove={handleMove}
              onTouchMove={handleMove}
            />
          </>
        )}
      </svg>
    </MarkerWrapper>
  );
};
